/**
 * M7 S12: the logic Missions 1 and 2 need beyond their data (spec §4, §5, §16.2). The missions themselves are data
 * (`src/data/story/missions/m1.json`, `m2.json`); this file holds what the runtime calls by name.
 *
 * - **Named payouts** (`m12Payouts`, passed to `missionContext`): the M1 bounty is `leverage.ts`'s Tethys offer at the
 *   career stage read at the moment of the fork (leased: min(15% of the principal, 2 payments), never clearing the
 *   lease, T8), as cash or as waived payments; `m2Watch` puts Watch book 7 in the ledger at the Reyes meeting and,
 *   when the career has logged a guardian-livery kill, the Holdfast log for the pilot Reyes names (spec §2.4).
 * - **The patrol scan of a contraband story item** (`contrabandScan`): a fine, never confiscation (SL-6, T6); at high
 *   Authority standing the patrol waves the player through and Hale's office logs the route (`hale_logged_route`).
 * - **Before Watchers** (`holdfastContractDone`): the 1st and 3rd Holdfast contracts handed in drop a guaranteed
 *   Watch log (W4). The +10 flat standing below 25 is `contracts.json factionStanding` (D-335 D4, `generate.ts`).
 * - **Keep the Candle lit** (`candleRunOffer`, `completeCandleRun`): Kell's repeatable Harrow delivery after help or
 *   stand off, never after a betrayal; no deadline.
 *
 * Pure: Career in, Career out; event-driven, never per frame. Tuning is in `contracts.json story` and the story data.
 */
import { withStanding, withWallet } from '../career/ops'
import type { Career } from '../career/types'
import contractsJson from '../data/contracts.json'
import { contractsFileSchema } from '../data/m2Schemas'
import { storyData } from './data'
import { holdHasContraband } from './items'
import { acceptTethysOffer, careerStage, tethysOffer, type OfferForm, type StageInput } from './leverage'
import { addLog, type LogsById } from './logs'
import type { PayoutFn } from './missions'
import type { LeverageConfig, ReactivationConfig } from './schemas'
import { hasFlag, setFlag, storyOf, withStory } from './state'
import type { StoryEffect, StoryResult } from './types'

export type M12Config = NonNullable<ReturnType<typeof contractsFileSchema.parse>['story']>

let cached: M12Config | null = null

/** `contracts.json story`, parsed once. */
export function m12Config(): M12Config {
  if (cached === null) {
    const story = contractsFileSchema.parse(contractsJson).story
    if (story === undefined) throw new Error('contracts.json: story block missing (M7 S12)')
    cached = story
  }
  return cached
}

/** Comms nodes the story link opens for M2 outside the step machine (`comms/m2.json`). */
export const M2_NODES = {
  /** Kell below Holdfast 25 at tier 3 (spec §5 trigger). */
  notYet: 'm2.not_yet',
  scanFine: 'm2.scan.fine',
  scanWaved: 'm2.scan.waved',
  candle: 'm2.candle',
  /** Reyes names one pilot, only when a guardian-livery kill is logged (spec §5 "the gut punch"). */
  named: 'm2.reyes.named',
} as const

/** The logs the Reyes meeting hands over (`evidence/watch.json`). */
export const M2_LOGS = {
  book: 'watch.book_seven',
  /** S37 (V-1): the Watch overlay, book 7's facing page; the story link generates its rows from the player's sales. */
  overlay: 'watch.book_seven_hulls',
  namedPilot: 'watch.reyes_named_pilot',
} as const

// ---------------------------------------------------------------------------------------------
// Named payouts
// ---------------------------------------------------------------------------------------------

/** The M1 bounty in `form` at the stage `stage` gives (read when the fork is taken). */
export function m1Bounty(stage: () => StageInput, form: OfferForm, cfg: LeverageConfig): PayoutFn {
  return (career) => {
    const offer = tethysOffer(careerStage(stage()), career, cfg)
    return acceptTethysOffer(career, offer, form)
  }
}

/** Watch book 7 and its facing page (the Watch overlay), plus the named pilot's log when a guardian-livery kill is on record. */
export function m2Watch(logs: LogsById): PayoutFn {
  return (career) => {
    let next = addLog(addLog(career, logs, M2_LOGS.book), logs, M2_LOGS.overlay)
    if ((career.guardianKills ?? []).length > 0) next = addLog(next, logs, M2_LOGS.namedPilot)
    return next
  }
}

/** Reyes's one line naming a pilot the player killed, or null when no guardian-livery kill is logged. */
export function m2NamedLine(career: Career): StoryEffect | null {
  if ((career.guardianKills ?? []).length === 0) return null
  return { type: 'comms', speaker: 'reyes', nodeId: M2_NODES.named }
}

/**
 * The payouts M1 and M2 name, for `missionContext(m12Payouts(...))`. `stage` is read at the moment of the fork
 * (the lease from the Career; the corporation and its fleet notes from the worker view).
 */
export function m12Payouts(
  stage: (career: Career) => StageInput,
  cfg: LeverageConfig = storyData().leverage,
  logs: LogsById = storyData().logsById,
): Readonly<Record<string, PayoutFn>> {
  return {
    m1BountyCash: (career) => m1Bounty(() => stage(career), 'cash', cfg)(career),
    m1BountyWaived: (career) => m1Bounty(() => stage(career), 'waived', cfg)(career),
    m2Watch: m2Watch(logs),
  }
}

/** A pilot without a corporation: the stage the lease gives (tests, and the link before the worker answers). */
export function pilotStage(career: Career): StageInput {
  return { leaseOwned: career.lease.owned, corpFaction: null, fleetNotes: 0 }
}

// ---------------------------------------------------------------------------------------------
// The patrol scan of a contraband story item
// ---------------------------------------------------------------------------------------------

export type ContrabandScan =
  /** Nothing contraband aboard: the scan has nothing to say about story items. */
  | { kind: 'clean' }
  /** Waved through at high Authority standing; Hale's office logs the route. */
  | { kind: 'waved'; result: StoryResult<Career> }
  /** Fined (as much of the fine as the wallet holds); the item stays aboard. Never confiscation. */
  | { kind: 'fined'; fine: number; result: StoryResult<Career> }

/** A finished patrol scan (`stepPatrolScan`) with a contraband story item aboard. */
export function contrabandScan(career: Career, cfg: M12Config = m12Config()): ContrabandScan {
  if (!holdHasContraband(career)) return { kind: 'clean' }
  if (career.standings.authority >= cfg.waveThroughStanding) {
    const effects: StoryEffect[] = [{ type: 'comms', speaker: 'charterPatrol', nodeId: M2_NODES.scanWaved }]
    return { kind: 'waved', result: { career: setFlag(career, 'hale_logged_route'), commands: [], effects } }
  }
  const fine = Math.min(cfg.contrabandFine, Math.max(0, career.wallet))
  const next = fine > 0 ? withWallet(career, -fine) : career
  const effects: StoryEffect[] = [
    { type: 'comms', speaker: 'charterPatrol', nodeId: M2_NODES.scanFine },
    { type: 'autosave', reason: 'story:contrabandFine' },
  ]
  return { kind: 'fined', fine, result: { career: next, commands: [], effects } }
}

// ---------------------------------------------------------------------------------------------
// Before Watchers: guaranteed Watch logs from Holdfast contracts
// ---------------------------------------------------------------------------------------------

/** A Holdfast contract was handed in: counts it and drops the guaranteed Watch log on the configured ordinals. */
export function holdfastContractDone(
  career: Career,
  cfg: M12Config = m12Config(),
  logs: LogsById = storyData().logsById,
): Career {
  const nth = (storyOf(career).holdfastContracts ?? 0) + 1
  let next = withStory(career, (s) => ({ ...s, holdfastContracts: nth }))
  for (const drop of cfg.holdfastWatchLogs) if (drop.nth === nth) next = addLog(next, logs, drop.log)
  return next
}

// ---------------------------------------------------------------------------------------------
// Keep the Candle lit (spec §16.2)
// ---------------------------------------------------------------------------------------------

/** Kell offers the run after help or stand off, never after a betrayal. */
export function candleRunAvailable(career: Career): boolean {
  if (hasFlag(career, 'watchers_betrayed')) return false
  return hasFlag(career, 'watchers_helped') || hasFlag(career, 'watchers_stood_off')
}

export interface CandleRunJob {
  target: string
  goods: readonly { good: string; qty: number }[]
}

/** The run's job (no deadline) and Kell's comms on the first offer, or null when it isn't available. */
export function candleRunOffer(career: Career, cfg: M12Config = m12Config()): { job: CandleRunJob; result: StoryResult<Career> } | null {
  if (!candleRunAvailable(career)) return null
  const first = !hasFlag(career, 'candle_run_offered')
  const effects: StoryEffect[] = first ? [{ type: 'comms', speaker: 'kell', nodeId: M2_NODES.candle }] : []
  return {
    job: { target: cfg.candleRun.target, goods: cfg.candleRun.goods },
    result: { career: setFlag(career, 'candle_run_offered'), commands: [], effects },
  }
}

/** A Candle run delivered: Holdfast standing and small credits. The Candle's blink never changes for it (PC-7). */
export function completeCandleRun(career: Career, pay: ReactivationConfig['candleRun'] = storyData().reactivation.candleRun): Career {
  if (!candleRunAvailable(career)) return career
  return withWallet(withStanding(career, 'holdfast', pay.holdfastStanding), pay.credits)
}
